import { useCallback, useEffect, useRef, useState } from 'react';

interface CameraState {
  stream: MediaStream | null;
  status: 'idle' | 'requesting' | 'granted' | 'denied' | 'error';
  error: string | null;
}

/**
 * The cleanup effect intentionally does NOT stop tracks — React Strict Mode
 * double-invokes effects in dev, and stopping a freshly-granted stream means
 * subsequent frames die. Tracks are stopped explicitly via `stop()` or by the
 * route that consumes the stream.
 */
export function useCamera() {
  const [state, setState] = useState<CameraState>({
    stream: null,
    status: 'idle',
    error: null,
  });
  const ref = useRef<MediaStream | null>(null);

  const request = useCallback(async (): Promise<MediaStream | null> => {
    if (ref.current) {
      // Already granted — surface the existing stream rather than re-prompting.
      const existing = ref.current;
      setState({ stream: existing, status: 'granted', error: null });
      return existing;
    }
    setState((s) => ({ ...s, status: 'requesting', error: null }));
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30, max: 60 },
          facingMode: 'user',
        },
        audio: false,
      });
      ref.current = stream;
      (stream as MediaStream & { __source?: 'self' | 'remote' }).__source = 'self';
      setState({ stream, status: 'granted', error: null });
      return stream;
    } catch (err) {
      const denied =
        err instanceof DOMException &&
        (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError');
      setState({
        stream: null,
        status: denied ? 'denied' : 'error',
        error: err instanceof Error ? err.message : 'unknown',
      });
      return null;
    }
  }, []);

  const stop = useCallback(() => {
    ref.current?.getTracks().forEach((t) => t.stop());
    ref.current = null;
    setState({ stream: null, status: 'idle', error: null });
  }, []);

  // No automatic stop on unmount — let the consumer decide. Strict-Mode-safe.
  useEffect(() => () => undefined, []);

  return { ...state, request, stop };
}
